import type { CrmZoneClick, Entity } from '@ps/web-shared-analytics';
import type { History } from 'history';
import type { Key } from 'path-to-regexp';
import { pathToRegexp } from 'path-to-regexp';

import { appRoutes, c2lPaths } from './routes';

export interface HandlerProps {
  path: Path;
  history: History;
  tracker?: (action: CrmZoneClick['action'], entities?: Entity[]) => void;
  navigate?: (pathName: string) => void | undefined;
}

export type Handler = (handlerProps: HandlerProps) => void;

export interface Route {
  path: string;
  handler?: Handler;
  leadToPRHub?: boolean;
}
export interface Path {
  path: string;
  url: string;
  params: Params;
}
interface MatchedRoute {
  route: Route;
  match: Path;
}
interface Params {
  [key: string]: string;
}

const stripC2l = (c2lPath: string) => {
  if (c2lPath.startsWith('://')) {
    return c2lPath.substring(3);
  } else if (c2lPath.startsWith('/')) {
    return c2lPath.substring(1);
  }
  return c2lPath;
};

const normalizePath = (path: string) => path.replace('://', '/');

const matchPath = (path: Path['path'], pathToTest: string): Path | null => {
  const keys: Key[] = [];
  const regexp = pathToRegexp(normalizePath(path), keys);
  const match = regexp.exec(normalizePath(pathToTest));

  if (!match) {
    return null;
  }

  const [url, ...values] = match;

  return {
    path, // the path pattern used to match
    url: path === '/' && url === '' ? '/' : url, // the matched portion of the URL
    params: keys.reduce((memo: Params, key, index) => {
      memo[key.name] = values[index];
      return memo;
    }, {})
  };
};

const matchStringToRoute = (fullUrl: string, routeList: Route[]): MatchedRoute | null => {
  const [pathPart, queryParams] = fullUrl.split('?');
  let res: MatchedRoute | null = null;

  routeList.forEach((route) => {
    const match = matchPath(route.path, pathPart);

    if (match && !res) {
      if (queryParams) {
        const params = new URLSearchParams(queryParams);
        match.params = { ...match.params, ...Object.fromEntries(params) };
      }
      res = { route, match };
    }
  });

  return res;
};

const matchRoute = (path: string): MatchedRoute | null => matchStringToRoute(path, appRoutes);

const matchC2L = (c2l: string): MatchedRoute | null => matchStringToRoute(c2l, c2lPaths);

export { matchRoute, matchC2L, stripC2l };
